'use client';

/**
 * CallToolTimeoutOffer — the voice-call surface's "half-look proof" first
 * screen of the tool-timeout recovery flow (design section 20b), shown for
 * BOTH ToolTimeoutView.OFFER and ToolTimeoutView.FAILED (see
 * call-tool-timeout-panel.tsx — the call surface collapses those two
 * text-chat cards into this one screen; only `retryAvailable` changes what
 * renders, matching the design brief's item 3). A caller is LISTENING, not
 * reading: one short line (Sara's spoken apology, condensed), one shorter
 * question, and two thumb-sized targets — no paragraph, no small print
 * above the action, unlike tool-timeout-offer.tsx/tool-timeout-failed.tsx's
 * StandardCard prose treatment.
 *
 * Deliberately NOT built on StandardCard (unlike every other leaf this flow
 * reuses) — that shell's eyebrow/icon-tile/body-prose anatomy is a
 * transcript-row convention; this screen has no eyebrow, no icon tile, and
 * exactly two lines of copy above two full-width 56px buttons. This is new
 * UI, not a restyle of an existing leaf (see this task's report for the
 * "reuse vs. build" call), so it lives in voice/ alongside the rest of the
 * call surface rather than beside the text-chat tool-timeout-*.tsx family.
 *
 * `retryAvailable=false` (the backend's retry cap is exhausted) hides "Try
 * again" ENTIRELY — never disables it — and "Leave my details" becomes the
 * sole, full-width action, mirroring tool-timeout-failed.tsx's identical
 * rule verbatim.
 *
 * `error` (session-recovery Finding 5) renders a small alert line BELOW the
 * two buttons rather than above them — this screen's whole brief is "no
 * small print above the action" for the ordinary case, but a failed SEND
 * (the socket wasn't open when the user tapped "Try again") is information
 * the user needs, not reassurance filler, so it earns a line rather than
 * being dropped silently. Mirrors tool-timeout-offer.tsx's identical prop.
 */
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { V3_COLORS, V3_SHADOWS } from '@/lib/design-tokens';

export interface CallToolTimeoutOfferProps {
  retryAvailable: boolean;
  onRetry: () => void;
  onLeaveDetails: () => void;
  /** Session-recovery Finding 5: set when the last "Try again" tap couldn't
   *  even SEND the retry_tool_call frame (the socket wasn't open) — see
   *  tool-timeout-offer.tsx's identical prop for the full rationale.
   *  `null`/undefined renders nothing. */
  error?: string | null;
}

/** Shared press feel (design-tokens.ts's PRESSABLE) folded into one
 *  transition list alongside `background-color`/`border-color` — see that
 *  token's own "GOTCHA" note on why a second `transition-*` utility can't
 *  just be appended when a control already animates color. */
const BUTTON_BASE =
  'flex h-[56px] w-full items-center justify-center gap-[8px] rounded-[16px] text-[15px] font-semibold ' +
  'transition-[background-color,border-color,transform] duration-[160ms] ease-out motion-safe:active:scale-[0.96]';

export function CallToolTimeoutOffer({
  retryAvailable,
  onRetry,
  onLeaveDetails,
  error,
}: CallToolTimeoutOfferProps) {
  return (
    <div className="flex w-full flex-col items-center gap-[16px]">
      <div className="flex flex-col items-center gap-[4px] text-center">
        <div
          className="font-[family-name:var(--font-lora)] text-[19px] font-semibold"
          style={{ color: V3_COLORS.ink }}
        >
          I couldn&apos;t pull that up.
        </div>
        <div
          className="max-w-[240px] text-[14px] leading-[1.45]"
          style={{ color: V3_COLORS.muted }}
        >
          Want me to try again, or have a live agent follow up?
        </div>
      </div>

      <div className="flex w-full flex-col gap-[10px]">
        {retryAvailable && (
          <button
            type="button"
            onClick={onRetry}
            className={cn(BUTTON_BASE, 'text-white')}
            style={{
              backgroundColor: V3_COLORS.orange,
              boxShadow: V3_SHADOWS.cta,
            }}
          >
            <RefreshCw aria-hidden="true" className="size-[18px]" />
            Try again
          </button>
        )}
        <button
          type="button"
          onClick={onLeaveDetails}
          className={cn(BUTTON_BASE, 'border-[1.5px] bg-white')}
          style={{
            borderColor: V3_COLORS.warmBorder,
            color: V3_COLORS.ink,
          }}
        >
          Leave my details
        </button>
      </div>

      {error && (
        <div
          role="alert"
          className="max-w-[240px] text-center text-[13px] leading-[1.4]"
          style={{ color: '#b3261e' }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
